export const FISH_PRODUCT_IDS = ["HAIRTAIL", "MACKEREL", "SQUID"] as const;

export type FishProductId = (typeof FISH_PRODUCT_IDS)[number];

export const FISH_PRICE_WON_VALUES = [1000, 2000, 3000, 4000, 5000] as const;

export type FishPriceWon = (typeof FISH_PRICE_WON_VALUES)[number];

export const DEFAULT_ROUND_COUNT = 4;
export const SALES_TICKETS_PER_PLAYER = 6;
export const MIN_PLAYER_COUNT = 3;
export const MAX_PLAYER_COUNT = 12;

export type FishShopV2Phase =
  | "TICKET_ALLOCATION"
  | "MARKET_REVEALED"
  | "PRICE_SUBMISSION"
  | "ROUND_REVEALED"
  | "RESTART_READY"
  | "COMPLETED";

export type FishShopV2Status = "IN_PROGRESS" | "COMPLETED";

export type FishShopV2ItemStatus = "AVAILABLE" | "USED_SECRET" | "USED_CHECK";

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export interface InitGameOptions {
  readonly playerIds: readonly string[];
  readonly hostPlayerId: string;
  readonly gameId?: string;
  readonly seed?: number;
  readonly roundCount?: number;
}

export type FishTicketsByProduct = Readonly<Record<FishProductId, number>>;

export type FishPricesByProduct = Readonly<
  Partial<Record<FishProductId, FishPriceWon>>
>;

export interface FishShopV2Player {
  readonly playerId: string;
  readonly remainingTickets: number;
  readonly totalIncomeWon: number;
  readonly itemStatus: FishShopV2ItemStatus;
  readonly checkSnapshotWon: number | null;
}

export interface FishProductMarketSeller {
  readonly playerId: string;
  readonly ticketCount: number;
}

export interface FishProductMarket {
  readonly productId: FishProductId;
  readonly sellers: readonly FishProductMarketSeller[];
  readonly soldTicketCount: number;
}

export type FishMarketByProduct = Readonly<
  Record<FishProductId, FishProductMarket>
>;

export interface FishRoundResult {
  readonly productId: FishProductId;
  readonly soldTicketCount: number;
  readonly finalPriceWon: FishPriceWon;
  readonly totalRevenueWon: number;
  readonly winnerIds: readonly string[];
  readonly payoutByPlayerId: Readonly<Record<string, number>>;
  readonly hidden: boolean;
}

export interface FishShopV2Round {
  readonly roundNumber: number;
  readonly phase: FishShopV2Phase;
  readonly allocations: Readonly<Record<string, FishTicketsByProduct>>;
  readonly market: FishMarketByProduct;
  readonly priceSubmissions: Readonly<Record<string, FishPricesByProduct>>;
  readonly secretSelections: Readonly<Record<string, FishProductId>>;
  readonly productResults: readonly FishRoundResult[];
  readonly incomeByPlayerId: Readonly<Record<string, number>>;
  readonly confirmedPlayerIds: readonly string[];
}

export interface FishShopV2State {
  readonly gameId: string;
  readonly status: FishShopV2Status;
  readonly roundCount: number;
  readonly currentRound: FishShopV2Round;
  readonly completedRounds: readonly FishShopV2Round[];
  readonly players: readonly FishShopV2Player[];
  readonly winnerIds: readonly string[];
  readonly lowestPlayerIds: readonly string[];
  readonly garnetAwardsByPlayerId: Readonly<Record<string, number>>;
  readonly restartNumber: number;
  readonly hostPlayerId: string;
  readonly seed: number;
}

export interface AllocateTicketsAction {
  readonly type: "AllocateTickets";
  readonly playerId: string;
  readonly ticketsByProduct: Readonly<Partial<Record<FishProductId, number>>>;
}

export interface LockMarketAction {
  readonly type: "LockMarket";
  readonly playerId: string;
}

export interface SubmitPricesAction {
  readonly type: "SubmitPrices";
  readonly playerId: string;
  readonly prices: FishPricesByProduct;
}

export interface UseSecretAction {
  readonly type: "UseSecret";
  readonly playerId: string;
  readonly productId: FishProductId;
}

export interface UseCheckAction {
  readonly type: "UseCheck";
  readonly playerId: string;
}

export interface RevealRoundAction {
  readonly type: "RevealRound";
  readonly playerId: string;
}

export interface ConfirmRoundResultAction {
  readonly type: "ConfirmRoundResult";
  readonly playerId: string;
}

export interface RestartGameAction {
  readonly type: "RestartGame";
  readonly playerId: string;
}

export type FishShopV2Action =
  | AllocateTicketsAction
  | LockMarketAction
  | SubmitPricesAction
  | UseSecretAction
  | UseCheckAction
  | RevealRoundAction
  | ConfirmRoundResultAction
  | RestartGameAction;

export interface MaskedFishRoundResult {
  readonly productId: FishProductId;
  readonly soldTicketCount: number;
  readonly finalPriceWon: FishPriceWon | null;
  readonly totalRevenueWon: number | null;
  readonly winnerIds: readonly string[] | null;
  readonly payoutByPlayerId: Readonly<Record<string, number>> | null;
  readonly hidden: boolean;
}

export interface FishShopV2ViewerPlayer {
  readonly playerId: string;
  readonly remainingTickets: number | null;
  readonly totalIncomeWon: number | null;
  readonly itemStatus: FishShopV2ItemStatus | null;
  readonly checkSnapshotWon: number | null;
}

export interface FishShopV2ViewerRound {
  readonly roundNumber: number;
  readonly phase: FishShopV2Phase;
  readonly allocatedPlayerIds: readonly string[];
  readonly ownAllocation: FishTicketsByProduct | null;
  readonly market: FishMarketByProduct;
  readonly priceSubmitterIds: readonly string[];
  readonly priceSubmittedPlayerIds: readonly string[];
  readonly ownPriceSubmission: FishPricesByProduct | null;
  readonly productResults: readonly MaskedFishRoundResult[];
  readonly confirmedPlayerIds: readonly string[];
}

export interface FishShopV2ViewerState {
  readonly gameId: string;
  readonly status: FishShopV2Status;
  readonly roundCount: number;
  readonly currentRound: FishShopV2ViewerRound;
  readonly players: readonly FishShopV2ViewerPlayer[];
  readonly winnerIds: readonly string[];
  readonly lowestPlayerIds: readonly string[];
  readonly garnetAwardsByPlayerId: Readonly<Record<string, number>>;
  readonly restartNumber: number;
  readonly hostPlayerId: string;
}
